import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Snackbar,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  AccountTree,
  Warning,
  Refresh,
  Search,
  Visibility,
  FilterList,
  ContentCopy,
} from '@mui/icons-material';
import { useTenant } from '../hooks/useTenant';
import { ProcessInstance, ProcessDefinition } from '../types/process';
import { useProcessesService } from '../services/processesService';

const DRAWER_WIDTH = 280;

// Função para truncar GUID (mostrar início + ... + fim)
const truncateGuid = (guid: string, startLength: number = 8, endLength: number = 8): string => {
  if (guid.length <= startLength + endLength + 3) {
    return guid;
  }
  return `${guid.substring(0, startLength)}...${guid.substring(guid.length - endLength)}`;
};

// Componente para exibir ID com tooltip e copiar
const GuidDisplay: React.FC<{ guid: string }> = ({ guid }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // Método moderno com fallback
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(guid);
      } else {
        // Fallback para navegadores mais antigos ou contextos inseguros
        const textArea = document.createElement('textarea');
        textArea.value = guid;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar GUID:', err);
      // Fallback adicional
      try {
        const textArea = document.createElement('textarea');
        textArea.value = guid;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy também falhou:', fallbackErr);
      }
    }
  };

  return (
    <Tooltip title={guid} placement="top" arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography 
          variant="body2" 
          fontFamily="monospace" 
          fontWeight={500}
          sx={{ 
            fontSize: '0.875rem',
            maxWidth: '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {truncateGuid(guid)}
        </Typography>
        <Tooltip title={copied ? "Copiado!" : "Copiar ID completo"} arrow>
          <IconButton 
            size="small" 
            onClick={handleCopy}
            sx={{ 
              padding: '2px',
              minWidth: '20px',
              width: '20px',
              height: '20px',
              opacity: 0.5,
              '&:hover': { 
                opacity: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <ContentCopy sx={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Tooltip>
  );
};

const ProcessInstances: React.FC = () => {
  const { currentTenant, isTenantSelected } = useTenant();
  const navigate = useNavigate();
  const processesService = useProcessesService();

  // Estados
  const [processInstances, setProcessInstances] = useState<ProcessInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchId, setSearchId] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    total: 0,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  
  // Estados dos filtros
  const [filters, setFilters] = useState({
    finished: false,
    active: false,
    suspended: false,
    withIncidents: false,
  });

  // Estados para processos disponíveis
  const [availableProcesses, setAvailableProcesses] = useState<ProcessDefinition[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<ProcessDefinition[]>([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);

  // Carregar processos disponíveis
  const loadAvailableProcesses = useCallback(async () => {
    if (!currentTenant) return;

    setLoadingProcesses(true);
    try {
      const response = await processesService.getProcessDefinitions(
        { tenantId: currentTenant.name },
        { page: 0, size: 100 } // Carregar todos os processos
      );
      setAvailableProcesses(response.definitions);
    } catch (err) {
      console.error('Erro ao carregar processos disponíveis:', err);
    } finally {
      setLoadingProcesses(false);
    }
  }, [currentTenant, processesService]);

  // Função para carregar instâncias de processo
  const loadProcessInstances = useCallback(async () => {
    if (!currentTenant) return;

    setLoading(true);
    setError(null);

    try {
      // Construir objeto de filtros
      const activeFilters: {
        finished?: boolean;
        active?: boolean;
        suspended?: boolean;
        withIncidents?: boolean;
        processKeys?: string[];
      } = {};
      
      if (filters.finished) activeFilters.finished = true;
      if (filters.active) activeFilters.active = true;
      if (filters.suspended) activeFilters.suspended = true;
      if (filters.withIncidents) activeFilters.withIncidents = true;
      
      // Adicionar filtro de processos selecionados
      if (selectedProcesses.length > 0) {
        activeFilters.processKeys = selectedProcesses.map(p => p.key);
      }
      
      const response = await processesService.getProcessInstances(
        currentTenant.name,
        { page: pagination.page, size: pagination.size },
        Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
        searchId || undefined
      );
      
      setProcessInstances(response.processInstances);
      setPagination(prev => ({ 
        ...prev, 
        total: response.pagination.total 
      }));
      
    } catch (err) {
      console.error('Erro ao carregar instâncias de processo:', err);
      setError('Erro ao carregar instâncias de processo. Tente novamente.');
      // Limpar dados em caso de erro
      setProcessInstances([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [currentTenant, pagination.page, pagination.size, searchId, filters, selectedProcesses, processesService]);

  // Carregar processos disponíveis quando o tenant muda
  useEffect(() => {
    if (currentTenant) {
      loadAvailableProcesses();
    }
  }, [currentTenant, loadAvailableProcesses]);

  // Carregar instâncias quando os filtros mudam
  useEffect(() => {
    loadProcessInstances();
  }, [loadProcessInstances]);

  const getStatusColor = (status: ProcessInstance['status']) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'SUSPENDED': return 'warning';
      case 'COMPLETED': return 'info';
      case 'EXTERNALLY_TERMINATED': return 'error';
      case 'INTERNALLY_TERMINATED': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: ProcessInstance['status']) => {
    switch (status) {
      case 'ACTIVE': return 'Ativo';
      case 'SUSPENDED': return 'Suspenso';
      case 'COMPLETED': return 'Concluído';
      case 'EXTERNALLY_TERMINATED': return 'Terminado Externamente';
      case 'INTERNALLY_TERMINATED': return 'Terminado Internamente';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchId(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (filterName: keyof typeof filters) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({ 
      ...prev, 
      size: parseInt(event.target.value, 10),
      page: 0 
    }));
  };

  const handleRefresh = () => {
    loadProcessInstances();
  };

  if (!isTenantSelected) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" icon={<Warning />}>
          Selecione um tenant para visualizar as instâncias de processos
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', ml: 12.5 }}>
      {/* Painel lateral de filtros */}
      <Box sx={{ 
        width: DRAWER_WIDTH,
        flexShrink: 0,
        borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#fafafa',
        overflowY: 'auto',
        height: '100vh'
      }}>
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList />
            <Typography variant="h6" fontWeight={600}>
              Filtros
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Filtro de processos */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Processos
            </Typography>
            <Autocomplete
              multiple
              options={availableProcesses}
              getOptionLabel={(option) => option.name || option.key}
              value={selectedProcesses}
              onChange={(_event, newValue) => {
                setSelectedProcesses(newValue);
                setPagination(prev => ({ ...prev, page: 0 }));
              }}
              loading={loadingProcesses}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Selecione processos..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProcesses ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.name || option.key}
                    size="small"
                    key={option.id}
                  />
                ))
              }
            />
            {selectedProcesses.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {selectedProcesses.length} processo(s) selecionado(s)
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Filtros de status */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Status
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.active}
                    onChange={() => handleFilterChange('active')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Ativas</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.suspended}
                    onChange={() => handleFilterChange('suspended')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Suspensas</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.finished}
                    onChange={() => handleFilterChange('finished')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Concluídas</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.withIncidents}
                    onChange={() => handleFilterChange('withIncidents')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Com Incidentes</Typography>}
              />
            </FormGroup>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Botão de limpar filtros */}
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={() => {
              setFilters({
                finished: false,
                active: false,
                suspended: false,
                withIncidents: false,
              });
              setSelectedProcesses([]);
              setSearchId('');
              setPagination(prev => ({ ...prev, page: 0 }));
            }}
          >
            Limpar Filtros
          </Button>
        </Box>
      </Box>

      {/* Conteúdo principal */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 0 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountTree />
              Instâncias de Processos
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitorando instâncias de processos do tenant: {currentTenant?.name}
            </Typography>
          </Box>

          {/* Barra de busca e ações */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar por ID da instância..."
            value={searchId}
            onChange={handleSearchChange}
            size="small"
            sx={{ flexGrow: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Atualizar'}
          </Button>
        </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* Tabela de instâncias */}
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 0 }}>
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome do Processo</TableCell>
                  <TableCell>Business Key</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Iniciado em</TableCell>
                  <TableCell>Finalizado em</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Carregando instâncias...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : processInstances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma instância encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  processInstances.map((processInstance) => (
                    <TableRow key={processInstance.id} hover>
                      <TableCell sx={{ position: 'relative' }}>
                        <GuidDisplay guid={processInstance.id} />
                      </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {processInstance.process_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {processInstance.business_key || '-'}
                      </Typography>
                    </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={getStatusLabel(processInstance.status)}
                            color={getStatusColor(processInstance.status) as 'success' | 'warning' | 'error' | 'info' | 'default'}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(processInstance.start_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {processInstance.end_time ? formatDate(processInstance.end_time) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Visualizar detalhes">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/process-instances/${processInstance.id}`)}
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Paginação */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.size}
            page={pagination.page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>

        {/* Snackbar para feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ProcessInstances;
